import React, { ReactNode, useCallback, useState } from 'react';
import { IResortReports } from 'api/resort/types';
import { BlockContent } from 'components/blocks/BlockContent';
import { Report } from 'components/resort/Report';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { useAnalytics } from 'util/analytics';
import { Diagnostics } from 'util/diagnostics';
import { fetchResortReports } from 'api/resort/static';
import classNames from 'classnames';
import Loader from 'react-spinners/PuffLoader';
import theme from 'theme';
import styles from './styles.module.scss';

export const ReportsList = ({
  resortList,
  uuid,
  className,
  aiAssistantWidget,
}: {
  resortList: IResortReports,
  uuid: string,
  className?: string,
  aiAssistantWidget?: ReactNode,
}) => {
  const { t } = useOTSTranslation('common');
  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [pagesResort, addPageResorts] = useState<Array<IResortReports>>([resortList]);
  const [currentPage, setCurrentPage] = useState<number>(1); // 1-based in V3 endpoint
  const isValidResponse = (response: IResortReports) => (
    response.pagination && response.reportsList
  );
  const a = useAnalytics();

  const loadNewReports = useCallback(async () => {
    a.viewContent({
      content: 'First Hand Reports',
    });
    toggleLoading(true);

    const nextPage = currentPage + 1;
    const newReportList: IResortReports = await fetchResortReports(uuid, nextPage, 10);

    if (!isValidResponse(newReportList)) {
      Diagnostics.error({ message: 'Server returned unknown report page type', tag: 'resort-reports' });
      return;
    }

    addPageResorts((prevPagesResort) => [...prevPagesResort, newReportList]);
    setCurrentPage(nextPage);
    toggleLoading(false);
  }, [a, toggleLoading, addPageResorts, currentPage, uuid]);

  const getLastPage = Math.ceil(pagesResort[0].pagination.count / pagesResort[0].pagination.limit);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <BlockContent className={styles.box}>
        {
          pagesResort.map((item, page) => {
            const insertWidgetAfter = page === 0 && aiAssistantWidget
              ? Math.min(2, item.reportsList.length - 1)
              : -1;
            return (
              <React.Fragment key={item.pagination.page}>
                {
                  item.reportsList.map((resort, i) => {
                    const globalIndex = page * 10 + i;
                    const showWidget = globalIndex === insertWidgetAfter;
                    return (
                      <React.Fragment key={resort.uuid}>
                        <Report report={resort} className={styles.report} />
                        {showWidget && aiAssistantWidget}
                      </React.Fragment>
                    );
                  })
                }
              </React.Fragment>
            );
          })
        }
        {aiAssistantWidget && pagesResort[0].reportsList.length === 0 && aiAssistantWidget}
        {
          pagesResort.length !== getLastPage ? (
            <button
              type="button"
              className={styles.loadBtn}
              onClick={loadNewReports}
              disabled={isLoading}
            >
              {
                isLoading ? (
                  <Loader
                    color={theme.white}
                    size={32}
                  />
                ) : t('common.loadMore')
              }
            </button>
          ) : <></>
        }
      </BlockContent>
    </div>
  );
};
